import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import api from "../services/api";

const Merchants: React.FC = () => {
  const [merchants, setMerchants] = useState<any[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);

  useEffect(() => {
    fetchMerchants();
  }, []);

  const fetchMerchants = async () => {
    try {
      const data = await api.getMerchants();
      setMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const columns: GridColDef[] = [
    { field: "companyName", headerName: "Company", width: 200 },
    { field: "displayName", headerName: "Display Name", width: 150 },
    { field: "contactEmail", headerName: "Email", width: 200 },
    { field: "contactPhone", headerName: "Phone", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "ACTIVE" ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 150,
      valueFormatter: (params) => `${params.value || 0} RUB`,
    },
    {
      field: "totalIncome",
      headerName: "Total Income",
      width: 150,
      valueFormatter: (params) => `${params.value || 0} RUB`,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedMerchant(params.row);
          }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Merchant Management
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Merchants
              </Typography>
              <Typography variant="h4">{merchants.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Merchants
              </Typography>
              <Typography variant="h4">
                {merchants.filter((m) => m.status === "ACTIVE").length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Balance
              </Typography>
              <Typography variant="h4">
                {merchants
                  .reduce((sum, m) => sum + (m.balance || 0), 0)
                  .toFixed(2)}{" "}
                RUB
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Income
              </Typography>
              <Typography variant="h4">
                {merchants
                  .reduce((sum, m) => sum + (m.totalIncome || 0), 0)
                  .toFixed(2)}{" "}
                RUB
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <DataGrid
          rows={merchants}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          autoHeight
          onRowClick={(params) => setSelectedMerchant(params.row)}
        />
      </Paper>

      <Dialog
        open={!!selectedMerchant}
        onClose={() => setSelectedMerchant(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Merchant Details: {selectedMerchant?.companyName}
        </DialogTitle>
        <DialogContent>
          {selectedMerchant && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="ID"
                        secondary={selectedMerchant.id}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Company Name"
                        secondary={selectedMerchant.companyName}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Display Name"
                        secondary={selectedMerchant.displayName}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Contact Email"
                        secondary={selectedMerchant.contactEmail}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Contact Phone"
                        secondary={selectedMerchant.contactPhone}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Status"
                        secondary={selectedMerchant.status}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Balance"
                        secondary={`${selectedMerchant.balance || 0} RUB`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Income"
                        secondary={`${selectedMerchant.totalIncome || 0} RUB`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Outcome"
                        secondary={`${selectedMerchant.totalOutcome || 0} RUB`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Created At"
                        secondary={new Date(
                          selectedMerchant.createdAt
                        ).toLocaleString()}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  API Configuration
                </Typography>
                <Card>
                  <CardContent>
                    <TextField
                      fullWidth
                      label="API Key"
                      value={selectedMerchant.apiKey || "Not set"}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <Tooltip title="Copy API Key">
                            <IconButton
                              onClick={() =>
                                copyToClipboard(selectedMerchant.apiKey)
                              }
                              edge="end"
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="Webhook URL"
                      value={selectedMerchant.webhookUrl || "Not set"}
                      InputProps={{
                        readOnly: true,
                      }}
                      margin="normal"
                    />
                    <TextField
                      fullWidth
                      label="Webhook Secret"
                      value={selectedMerchant.webhookSecret || "Not set"}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <Tooltip title="Copy Webhook Secret">
                            <IconButton
                              onClick={() =>
                                copyToClipboard(selectedMerchant.webhookSecret)
                              }
                              edge="end"
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                      margin="normal"
                    />
                  </CardContent>
                </Card>
              </Box>

              {selectedMerchant.user && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Card>
                    <CardContent>
                      <Typography variant="body2">
                        <strong>Username:</strong>{" "}
                        {selectedMerchant.user.username}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Role:</strong> {selectedMerchant.user.role}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Merchants;
